import { useEffect, useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { AuthContext } from "../../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../../utils/constants/permissions";
import { exportTable } from "../../../../utils/exportTable";
import { btn } from "../../../../components/Button/buttonStyles";

const HEADERS = [
  "Code",
  "Type",
  "Name",
  // "Services",
  // "Specializations",
  "Primary User",
  "Language Pairs",
  "Country",
  "Assignable",
  "Tags",
  "Created At",
  "Invited By",
  "Registered At",
  "Last Activity At",
];

const TYPE_STYLES = {
  "In-House": "border-l-2 border-sky-400",
  Freelance: "border-l-2 border-emerald-400",
  Company: "border-l-2 border-amber-400",
};

const TYPE_BADGE = {
  "In-House": { label: "In-House", cls: "bg-sky-50 text-sky-700" },
  Freelance: { label: "Freelance", cls: "bg-emerald-50 text-emerald-700" },
  Company: { label: "Company", cls: "bg-amber-50 text-amber-700" },
};

const renderTypeBadge = (type) => {
  const badge = TYPE_BADGE[type];
  if (!badge) return <span className="text-gray-500">—</span>;
  return (
    <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}>
      {badge.label}
    </span>
  );
};

// Mirrors HEADERS above. Language Pairs, Tags, Invited By, Registered At and
// Last Activity At are still placeholders in the table, so they export empty.
const EXPORT_COLUMNS = [
  { header: "Code", value: (v) => `VDR-${String(v.id).padStart(3, "0")}` },
  { header: "Type", value: (v) => TYPE_BADGE[v.type]?.label || "" },
  { header: "Name", value: (v) => v.company_name || "" },
  {
    header: "Primary User",
    value: (v) =>
      `${v.primary_users?.first_name || ""} ${v.primary_users?.last_name || ""}`.trim(),
  },
  { header: "Language Pairs", value: () => "" },
  { header: "Country", value: (v) => v.country || "" },
  { header: "Assignable", value: (v) => (v.assignable_to_jobs ? "Yes" : "No") },
  { header: "Tags", value: () => "" },
  {
    header: "Created At",
    value: (v) => (v.createdAt ? new Date(v.createdAt).toLocaleDateString() : ""),
  },
  { header: "Invited By", value: () => "" },
  { header: "Registered At", value: () => "" },
  { header: "Last Activity At", value: () => "" },
];

const VendorsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.VENDORS);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await api.get("/vendors", { withCredentials: true });
        setVendors(response.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch vendors");
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Vendors</h1>
        <div className="flex items-center gap-2 text-sm">
          {!readOnly && (
            <button
              onClick={() => navigate("/vendors/create-vendor")}
              className={btn("primary", "sm")}
            >
              New Vendor
            </button>
          )}
          {!readOnly && (
            <button className={btn("secondary", "sm")}>
              Import from Excel
            </button>
          )}
          <button
            onClick={() => exportTable("vendors", EXPORT_COLUMNS, vendors)}
            className={btn("secondary", "sm")}
          >
            Export to Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}
      {error && <p className="text-danger-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{vendors.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[1500px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vendors.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No vendors found.
                      </td>
                    </tr>
                  ) : (
                    vendors.map((vendor, index) => {
                      const primaryUser = vendor.primary_users || {};
                      const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                      const typeStyle = TYPE_STYLES[vendor.type] ?? "";
                      return (
                        <tr
                          key={vendor.id}
                          className={`${rowBg} ${typeStyle} hover:bg-brand-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/vendors/${vendor.id}`}
                              className="text-brand-600 hover:text-brand-800 underline font-medium"
                            >
                              {`VDR-${String(vendor.id).padStart(3, "0")}`}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            {renderTypeBadge(vendor.type)}
                          </td>
                          
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {vendor.company_name || "—"}
                          </td>
                          {/* <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">-</td> */}
                          {/* <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">-</td> */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {`${primaryUser.first_name || ""} ${primaryUser.last_name || ""}`.trim() || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">—</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {vendor.country || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {vendor.assignable_to_jobs ? "Yes" : "No"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">—</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">
                            {vendor.createdAt
                              ? new Date(vendor.createdAt).toLocaleDateString()
                              : "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">—</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">—</td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-500">—</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorsPage;
